import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { GenericPageError } from '@components/core-ui/GenericPageError';
import { Wrapper } from '@modals/Admin/AdminBackupFilesModal/styles/Wrapper';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { useAPI } from '@providers/useAPI';
import { useModalContext } from '@providers/ModalProvider';
import { useMutation, useQuery } from '@tanstack/react-query';
import { JobsService } from 'app/API';
import { useNotifyQueryError } from 'app/hooks/useNotifyQueryError';
import { notifyApiCallError } from 'app/http/notifyApiCallError';
import { createFetchJobBackupFilesQueryFn } from 'app/queries/jobs/createFetchJobBackupFilesQueryFn';
import { createFetchMixdownStatusQueryFn } from 'app/queries/jobs/createFetchMixdownStatusQueryFn';
import { jobsKeys } from 'app/queries/jobs/jobsKeys';
import { useEffect } from 'react';

const MIXDOWN_STATUS_POLL_INTERVAL_MS = 10000;

type TProps = {
  onCancel: () => void;
  onSuccess: () => void;
  workspaceId: string;
  id: string;
};

export const BackupFilesModal = ({ onCancel, onSuccess, workspaceId, id }: TProps) => {
  const { call } = useAPI();
  const { closeModal, openModal } = useModalContext();

  const filesQuery = useQuery({
    queryKey: jobsKeys.backupFilesByJob(id, workspaceId),
    queryFn: createFetchJobBackupFilesQueryFn(call),
  });
  useNotifyQueryError(filesQuery.error);

  const mixdownMutation = useMutation({
    mutationFn: () => call(() => JobsService.mixdownJobBackupFiles({ wsid: workspaceId, jobId: id })),
    onError: notifyApiCallError,
  });
  const mixdownJobId = mixdownMutation.data;

  const mixdownStatusQuery = useQuery({
    queryKey: jobsKeys.mixdownStatus(workspaceId, mixdownJobId ?? ''),
    queryFn: createFetchMixdownStatusQueryFn(call),
    enabled: !!mixdownJobId,
    refetchInterval: (query) =>
      query.state.status === 'error' || query.state.data?.returnvalue || query.state.data?.failedReason
        ? false
        : MIXDOWN_STATUS_POLL_INTERVAL_MS,
  });
  useNotifyQueryError(mixdownStatusQuery.error);

  const mixdownStatus = mixdownStatusQuery.data;

  useEffect(() => {
    if (mixdownStatus?.returnvalue) {
      closeModal();
      onSuccess();
      return;
    }
    if (mixdownStatus?.failedReason) {
      openModal(ModalNamesEnum.GenericErrorModal, {
        message: 'Backup Files Processing Error',
      });
    }
  }, [mixdownStatus, closeModal, onSuccess, openModal]);

  const files = filesQuery.data ?? [];
  const isLoading =
    filesQuery.isLoading || mixdownMutation.isPending || (!!mixdownJobId && !mixdownStatusQuery.isError);

  if (!isLoading && !files.length) {
    return (
      <Wrapper>
        <ModalWrapper width="400px">
          <ModalTitle>Attach Backup Files</ModalTitle>

          <GenericPageError message="No files to attach" />

          <div>
            <Button onClick={onCancel}>Ok</Button>
          </div>
        </ModalWrapper>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <ModalWrapper width="400px">
        {isLoading ? <AbsoluteSpinner overlay={true} /> : null}
        <ModalTitle>Attach Backup Files</ModalTitle>

        <div className="files">
          {files.map((file, index) => (
            <div key={`${file}-${index}`} className="file">
              {file}
            </div>
          ))}
        </div>

        <div className="flex gap-2">
          <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
          <Button disabled={isLoading} onClick={() => mixdownMutation.mutate()}>
            Attach
          </Button>
        </div>
      </ModalWrapper>
    </Wrapper>
  );
};
